import React from 'react';

const buildImageSrc = (structuredData) => {
    const url = typeof structuredData?.url === 'string' ? structuredData.url.trim() : '';
    if (url) {
        return url;
    }

    const base64 = typeof structuredData?.base64 === 'string'
        ? structuredData.base64.trim()
        : typeof structuredData?.imageBase64 === 'string' ? structuredData.imageBase64.trim() : '';

    if (!base64) {
        return '';
    }

    const mimeType = typeof structuredData?.mimeType === 'string' && structuredData.mimeType.trim().length > 0
        ? structuredData.mimeType.trim()
        : 'image/png';

    return `data:${mimeType};base64,${base64}`;
};

const toDownloadName = (value, fallback) => {
    const normalized = typeof value === 'string' ? value.trim() : '';
    if (!normalized) {
        return fallback;
    }

    return normalized
        .toLowerCase()
        .replace(/\s+/g, '-')
        .replace(/[^a-z0-9-_]/g, '') || fallback;
};

const DownloadIcon = () => {
    return (
        <svg viewBox='0 0 24 24' width='16' height='16' aria-hidden='true' focusable='false'>
            <path d='M12 3v11' fill='none' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' />
            <path d='M8 11l4 4 4-4' fill='none' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round' />
            <path d='M5 20h14' fill='none' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' />
        </svg>
    );
};

const triggerDownload = ({ href, fileName, target = '_self' }) => {
    const link = document.createElement('a');
    link.href = href;
    link.download = fileName;
    link.target = target;
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

const downloadImageWithoutNavigation = async ({ imageSrc, imageName }) => {
    if (typeof imageSrc !== 'string' || imageSrc.trim().length === 0) {
        return;
    }

    if (imageSrc.startsWith('data:') || imageSrc.startsWith('blob:')) {
        triggerDownload({ href: imageSrc, fileName: imageName });
        return;
    }

    try {
        const response = await fetch(imageSrc);
        if (!response.ok) {
            throw new Error('Image download failed');
        }

        const blob = await response.blob();
        const objectUrl = URL.createObjectURL(blob);
        triggerDownload({ href: objectUrl, fileName: imageName });
        URL.revokeObjectURL(objectUrl);
    } catch (error) {
        // Keep current page intact; fallback opens a new tab if browser blocks direct download.
        triggerDownload({ href: imageSrc, fileName: imageName, target: '_blank' });
    }
};

const ImageMessage = ({ structuredData, labels }) => {
    const imageSrc = buildImageSrc(structuredData);
    const altText = typeof structuredData?.alt === 'string' && structuredData.alt.trim().length > 0
        ? structuredData.alt.trim()
        : labels.assistantImage;
    const caption = typeof structuredData?.caption === 'string' ? structuredData.caption.trim() : '';
    const imageName = `${toDownloadName(altText, 'assistant-image')}.png`;

    const handleDownloadClick = async () => {
        await downloadImageWithoutNavigation({ imageSrc, imageName });
    };

    if (!imageSrc) {
        return (
            <div className='copilot-chat-message copilot-chat-message--structured assistant'>
                <div className='copilot-chat-media-fallback'>{labels.imageUnavailable}</div>
            </div>
        );
    }

    return (
        <div className='copilot-chat-message copilot-chat-message--structured assistant'>
            <div className='copilot-chat-media-card'>
                <div className='copilot-chat-media-thumb-wrap'>
                    <img className='copilot-chat-media-thumb' src={imageSrc} alt={altText} loading='lazy' />
                    <button
                        type='button'
                        className='copilot-chat-media-thumb-download'
                        onClick={handleDownloadClick}
                        aria-label={`${labels.download} ${altText}`}
                        title={labels.download}
                    >
                        <DownloadIcon />
                    </button>
                </div>
                <div className='copilot-chat-media-content'>
                    <div className='copilot-chat-media-title'>{labels.image}</div>
                    {caption && <div className='copilot-chat-media-caption'>{caption}</div>}
                </div>
            </div>
        </div>
    );
};

export default ImageMessage;
